<template>
  <b-modal
    :visible="value"
    size="lg"
    centered
    :title="`${$t('Resumen del periodo')} - ${monthName(period.month)} ${period.year}`"
    @change="(val) => $emit('input', val)"
    hide-footer
  >
    <b-overlay :show="loading">
      <div v-if="summary" class="mb-2">
        <b-row>
          <b-col md="3">
            <b-card class="text-center mb-0" bg-variant="light-primary">
              <h3 class="mb-0">{{ summary.total_planillas }}</h3>
              <small>{{ $t("Total Planillas") }}</small>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="text-center mb-0" bg-variant="light-success">
              <h3 class="mb-0">{{ summary.total_aprobadas }}</h3>
              <small>{{ $t("Aprobadas") }}</small>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="text-center mb-0" bg-variant="light-warning">
              <h3 class="mb-0">{{ summary.total_pendientes }}</h3>
              <small>{{ $t("Pendientes") }}</small>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="text-center mb-0" bg-variant="light-danger">
              <h3 class="mb-0">{{ summary.total_anuladas }}</h3>
              <small>{{ $t("Anuladas") }}</small>
            </b-card>
          </b-col>
        </b-row>
        <div class="text-right font-weight-bold mt-1">
          {{ $t("Monto total pagado") }}: S/. {{ formatCurrency(summary.monto_total_pagado) }}
        </div>
      </div>

      <b-table
        :items="workers"
        :fields="fields"
        show-empty
        responsive
        striped
        small
        sticky-header="400px"
      >
        <template #cell(status_name)="{ item }">
          <b-badge pill :variant="resolveStypeStatus(item.status_id)" class="px-1 py-50">
            <feather-icon icon="InfoIcon" size="12" class="mr-25" />
            {{ resolveStatusLabel(item.status_name) }}
          </b-badge>
        </template>

        <template #cell(monto)="{ item }">
          S/. {{ formatCurrency(item.monto) }}
        </template>
      </b-table>

      <div v-if="period.status === 1" class="d-flex justify-content-end mt-2">
        <b-button variant="danger" :disabled="isBusy" @click="confirmClose">
          {{ $t("Confirmar cierre de periodo") }}
        </b-button>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import PayrollPeriodService from "@/views/rrhh/views/payroll/services/payroll-period.service";

export default {
  name: "ClosePeriodSummaryModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    period: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      isBusy: false,
      summary: null,
      workers: [],
      fields: [
        { key: "tipo", label: "Tipo" },
        { key: "empleado", label: "Trabajador" },
        { key: "status_name", label: "Estado" },
        { key: "monto", label: "Monto" },
      ],
      months: [
        { id: 1, label: "Enero" },
        { id: 2, label: "Febrero" },
        { id: 3, label: "Marzo" },
        { id: 4, label: "Abril" },
        { id: 5, label: "Mayo" },
        { id: 6, label: "Junio" },
        { id: 7, label: "Julio" },
        { id: 8, label: "Agosto" },
        { id: 9, label: "Septiembre" },
        { id: 10, label: "Octubre" },
        { id: 11, label: "Noviembre" },
        { id: 12, label: "Diciembre" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
  },
  watch: {
    value(isVisible) {
      if (isVisible) {
        this.fetchSummary();
      }
    },
  },
  created() {
    if (this.value) {
      this.fetchSummary();
    }
  },
  methods: {
    monthName(month) {
      const found = this.months.find((m) => m.id === Number(month));
      return found ? found.label : month;
    },

    formatCurrency(value) {
      return Number(value ?? 0).toFixed(2);
    },

    resolveStypeStatus(status_id) {
      let _style = "light-secondary";
      switch (status_id) {
        case 1:
          _style = "light-secondary";
          break;
        case 2:
          _style = "light-primary";
          break;
        case 3:
          _style = "light-success";
          break;
        case 4:
          _style = "light-secondary";
          break;
        case 5:
          _style = "light-primary";
          break;
        case 6:
          _style = "light-success";
          break;
        case 7:
        case 8:
          _style = "light-danger";
          break;
      }
      return _style;
    },

    resolveStatusLabel(payroll_status) {
      switch (payroll_status) {
        case "Fortnight Pending":
          return "Quincena Pendiente";
        case "Fortnight Approved":
          return "Quincena Aprobada";
        case "Fortnight Paid":
          return "Quincena Pagada";
        case "Salary Pending":
          return "Planilla Pendiente";
        case "Salary Approved":
          return "Planilla Aprobada";
        case "Salary Paid":
          return "Planilla Pagada";
        case "Fortnight Voided":
          return "Quincena Anulada";
        case "Salary Voided":
          return "Planilla Anulada";
        default:
          return "Estado no encontrado";
      }
    },

    async fetchSummary() {
      try {
        this.loading = true;
        const { data } = await PayrollPeriodService.getClosePeriodSummary(this.period.id);
        this.summary = data.summary;
        this.workers = data.workers;
      } catch (error) {
        this.showToastError(
          error.response?.data || "Error al obtener el resumen del periodo."
        );
      } finally {
        this.loading = false;
      }
    },

    async confirmClose() {
      const confirm = await this.showConfirmSwal(
        this.$t("¿Confirma el cierre del periodo?"),
        this.$t(
          "Esta acción es irreversible. Las planillas de este periodo ya no podrán modificarse."
        ),
        {
          confirmButtonText: this.$t("Sí, cerrar periodo"),
          cancelButtonText: this.$t("Cancelar"),
          customClass: {
            confirmButton: "btn btn-outline-danger mr-1",
            cancelButton: "btn btn-primary",
          },
        }
      );

      if (!confirm.isConfirmed) return;

      try {
        this.isBusy = true;
        this.addPreloader();
        await PayrollPeriodService.closePeriod(this.period.id, {
          user_id: this.currentUser.user_id,
        });
        this.showToastSuccess("Periodo cerrado correctamente.");
        this.$emit("closed");
      } catch (error) {
        this.showToastError(
          error.response?.data || "Error al cerrar el periodo."
        );
      } finally {
        this.isBusy = false;
        this.removePreloader();
      }
    },
  },
};
</script>
